/**
 * Gallery scene: workspace Only vs collapse, with tab-bar motion
 * (?test=maater-ui#tab-groups)
 *
 * Only parks the other workspace (tabs leave the bar). Collapse folds tabs into
 * the initials badge while staying in that workspace. Undo toast is quiet.
 */
import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  TAB_GROUP_MOTION_MS,
  tabGroupCollapseStripClass,
  tabGroupEnterClass,
  tabGroupExitClass,
  tabGroupMotionDurationMs,
} from '@/lib/tab-group-motion'
import { useTabGroupStripWidth } from '@/hooks/use-tab-group-strip-width'
import { SHELL_COLUMN_HEADER_CLASS } from '@/lib/shell-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type ProtoWs = {
  id: string
  name: string
  initials: string
  color: string
  tabs: { id: string; title: string }[]
}

const HOP: ProtoWs = {
  id: 'ws-hop',
  name: 'Hop Studios',
  initials: 'HS',
  color: '#c47a2c',
  // Long strip (8+) teaches group border spanning full content width + right-edge glow room.
  tabs: [
    { id: 'hop-1', title: 'Late-todo reminder' },
    { id: 'hop-2', title: 'Password docs' },
    { id: 'hop-3', title: 'Travis check-in' },
    { id: 'hop-4', title: 'Migrations talk' },
    { id: 'hop-5', title: 'Define schema' },
    { id: 'hop-6', title: 'EECON notes' },
    { id: 'hop-7', title: 'Daily scrum' },
    { id: 'hop-8', title: 'Projects index' },
  ],
}

const PERSONAL: ProtoWs = {
  id: 'ws-personal',
  name: 'Personal',
  initials: 'P',
  color: '#c44d7a',
  tabs: [{ id: 'per-1', title: 'volleyball notes' }],
}

type ScopeState = {
  activeIds: string[]
  collapsed: Set<string>
  activeTabId: string
  exitingIds: Set<string>
  enteringIds: Set<string>
}

const INITIAL: ScopeState = {
  activeIds: [HOP.id, PERSONAL.id],
  collapsed: new Set(),
  activeTabId: 'hop-1',
  exitingIds: new Set(),
  enteringIds: new Set(),
}

function ProtoGroupHeader({
  label,
  initials,
  tabCount,
  isCollapsed,
  onToggle,
}: {
  label: string
  initials: string
  tabCount: number
  isCollapsed: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="tab-group__header flex items-center gap-0 px-0 py-0 mb-px border-none cursor-pointer bg-transparent shrink-0"
      title={
        isCollapsed
          ? `${label} (${tabCount} tabs) - click to expand`
          : `${label} - click to collapse`
      }
      data-testid={testIds.maaterUiGallery.tabGroupsCollapse(initials)}
    >
      <span
        className="tab-group__badge inline-flex items-center justify-center px-1.5 py-0.5 rounded-t text-[10px] font-bold leading-none tracking-wide"
        style={{ minWidth: '1.5rem' }}
      >
        {isCollapsed ? `${initials} ${tabCount}` : initials}
      </span>
    </button>
  )
}

function ProtoTab({
  title,
  isActive,
  onClick,
}: {
  title: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <div className="tab-group__tab-wrap relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'tab-group__tab flex items-center gap-1 px-2 py-1.5 min-w-0 flex-shrink-0 relative rounded-t-lg text-xs',
          isActive && 'tab-group__tab--active text-[hsl(var(--tab-active-text))]',
          !isActive && 'text-[hsl(var(--tab-inactive-text))]',
        )}
      >
        <span className="truncate max-w-[7rem]">{title}</span>
      </button>
    </div>
  )
}

function ProtoTabGroupStrip({
  isCollapsed,
  tabCount,
  children,
}: {
  isCollapsed: boolean
  tabCount: number
  children: ReactNode
}) {
  const [stripRef, stripWidthPx] = useTabGroupStripWidth([tabCount, isCollapsed])
  return (
    <div
      ref={stripRef}
      className={cn(
        'tab-group__strip flex items-end gap-0.5',
        tabGroupCollapseStripClass(isCollapsed),
      )}
      style={
        stripWidthPx != null
          ? ({ '--tab-group-strip-w': `${stripWidthPx}px` } as CSSProperties)
          : undefined
      }
    >
      {children}
    </div>
  )
}

export function TabGroupsScene() {
  const [state, setState] = useState<ScopeState>(INITIAL)

  const visibleWorkspaces = useMemo(() => {
    const all = [HOP, PERSONAL]
    return all.filter(
      (w) => state.activeIds.includes(w.id) || state.exitingIds.has(w.id),
    )
  }, [state.activeIds, state.exitingIds])

  const runExitThenRemove = useCallback((removeId: string, nextIds: string[], nextTab: string) => {
    const duration = tabGroupMotionDurationMs()
    setState((prev) => ({
      ...prev,
      exitingIds: new Set([...prev.exitingIds, removeId]),
    }))
    window.setTimeout(() => {
      setState((prev) => {
        const exiting = new Set(prev.exitingIds)
        exiting.delete(removeId)
        return {
          ...prev,
          activeIds: nextIds,
          activeTabId: nextTab,
          exitingIds: exiting,
        }
      })
    }, duration)
  }, [])

  const runEnter = useCallback((enterId: string) => {
    const duration = tabGroupMotionDurationMs()
    setState((prev) => ({
      ...prev,
      activeIds: prev.activeIds.includes(enterId)
        ? prev.activeIds
        : [...prev.activeIds, enterId],
      enteringIds: new Set([...prev.enteringIds, enterId]),
    }))
    window.setTimeout(() => {
      setState((prev) => {
        const entering = new Set(prev.enteringIds)
        entering.delete(enterId)
        return { ...prev, enteringIds: entering }
      })
    }, duration)
  }, [])

  const handleOnly = useCallback(
    (targetId: string) => {
      const target = targetId === HOP.id ? HOP : PERSONAL
      const other = targetId === HOP.id ? PERSONAL : HOP
      if (state.activeIds.length === 1 && state.activeIds[0] === targetId) return

      const previousIds = [...state.activeIds]
      const previousTab = state.activeTabId

      const nextTab =
        target.tabs.find((t) => t.id === state.activeTabId)?.id ??
        target.tabs[0]?.id ??
        'home'

      if (state.activeIds.includes(other.id)) {
        runExitThenRemove(other.id, [targetId], nextTab)
      } else {
        setState((prev) => ({
          ...prev,
          activeIds: [targetId],
          activeTabId: nextTab,
        }))
      }

      toast(`Switched to ${target.name}`, {
        duration: 4000,
        className: 'tab-groups-quiet-toast',
        action: {
          label: 'Undo',
          onClick: () => {
            for (const id of previousIds) {
              if (id !== targetId) runEnter(id)
            }
            setState((prev) => ({
              ...prev,
              activeIds: previousIds,
              activeTabId: previousTab,
            }))
          },
        },
      })
    },
    [runEnter, runExitThenRemove, state.activeIds, state.activeTabId],
  )

  const toggleCollapse = (wsId: string) => {
    setState((prev) => {
      const next = new Set(prev.collapsed)
      if (next.has(wsId)) next.delete(wsId)
      else next.add(wsId)
      return { ...prev, collapsed: next }
    })
  }

  const restoreHopCollapsed = () => {
    setState((prev) => {
      const collapsed = new Set(prev.collapsed)
      collapsed.add(HOP.id)
      return { ...prev, collapsed }
    })
    if (!state.activeIds.includes(HOP.id)) {
      runEnter(HOP.id)
    }
  }

  const reset = () => {
    setState(INITIAL)
  }

  // Same rule as DocumentTabs: wash the bar only when a single workspace is in scope.
  const focusWorkspace =
    state.activeIds.length === 1
      ? visibleWorkspaces.find((w) => w.id === state.activeIds[0]) ?? null
      : null
  const activeTitle =
    state.activeTabId === 'home'
      ? 'Home'
      : [...HOP.tabs, ...PERSONAL.tabs].find((t) => t.id === state.activeTabId)?.title ??
        'Document'

  return (
    <div className="space-y-4" data-testid={testIds.maaterUiGallery.tabGroupsScene}>
      <p className="text-sm" style={{ color: 'var(--ink)' }}>
        <strong className="font-medium">Only</strong> parks the other workspace (tabs leave
        the bar; quiet toast + Undo). <strong className="font-medium">Collapse</strong> folds
        tabs into the initials badge while you stay in that workspace. Motion:{' '}
        <code className="text-xs">@/lib/tab-group-motion</code> ({TAB_GROUP_MOTION_MS}ms).
        Reduced motion = instant. Chrome is the production tab bar (glow, group
        line, workspace wash, active ease matching this motion) - this scene
        teaches Only / collapse. Open rules (production, `resolveOpenIntent`):
        tabs stay until closed; plain click replaces the current tab; Cmd/Ctrl+click
        opens a new tab; a pinned tab is never replaced. Double-click a tab to pin.
        Reload continuity is production-only (see maater-design-ui Primitive table).
      </p>

      <div className="overflow-hidden rounded-md">
      <div
        className={cn(
          'document-tabs-bar relative flex w-full items-end overflow-hidden border-b border-border',
          SHELL_COLUMN_HEADER_CLASS,
          focusWorkspace && 'document-tabs--workspace-focus',
        )}
        data-testid={testIds.maaterUiGallery.tabGroupsBar}
        style={{
          ...(focusWorkspace
            ? ({ '--ws-color': focusWorkspace.color } as CSSProperties)
            : { backgroundColor: 'hsl(var(--tab-bar-bg))' }),
        }}
      >
        <div className="flex h-full min-w-0 flex-1 items-end gap-0.5 self-stretch pl-1 pr-2 flex-nowrap overflow-x-auto">
          <ProtoTab
            title="Home"
            isActive={state.activeTabId === 'home'}
            onClick={() => setState((p) => ({ ...p, activeTabId: 'home' }))}
          />
          {visibleWorkspaces.map((ws) => {
            const isCollapsed = state.collapsed.has(ws.id)
            const isExiting = state.exitingIds.has(ws.id)
            const isEntering = state.enteringIds.has(ws.id)
            return (
              <div
                key={ws.id}
                className={cn(
                  'tab-group flex items-end gap-0.5 flex-nowrap',
                  ws.tabs.some((tab) => tab.id === state.activeTabId) && 'tab-group--has-active',
                  isCollapsed && 'tab-group--collapsed',
                  isExiting && tabGroupExitClass(),
                  isEntering && tabGroupEnterClass(),
                )}
                style={{ '--ws-color': ws.color } as CSSProperties}
                data-testid={testIds.maaterUiGallery.tabGroupsGroup(ws.id)}
              >
                <ProtoGroupHeader
                  label={ws.name}
                  initials={ws.initials}
                  tabCount={ws.tabs.length}
                  isCollapsed={isCollapsed}
                  onToggle={() => toggleCollapse(ws.id)}
                />
                <ProtoTabGroupStrip isCollapsed={isCollapsed} tabCount={ws.tabs.length}>
                  {ws.tabs.map((tab) => (
                    <ProtoTab
                      key={tab.id}
                      title={tab.title}
                      isActive={state.activeTabId === tab.id}
                      onClick={() => setState((p) => ({ ...p, activeTabId: tab.id }))}
                    />
                  ))}
                </ProtoTabGroupStrip>
              </div>
            )
          })}
        </div>
      </div>
      <div
        className="px-4 py-5 text-sm text-foreground"
        style={{ backgroundColor: 'hsl(var(--tab-active-bg))' }}
      >
        {activeTitle}
      </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted/60"
          style={{ borderColor: 'var(--line)' }}
          data-testid={testIds.maaterUiGallery.tabGroupsOnlyPersonal}
          onClick={() => handleOnly(PERSONAL.id)}
        >
          Only Personal
        </button>
        <button
          type="button"
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted/60"
          style={{ borderColor: 'var(--line)' }}
          data-testid={testIds.maaterUiGallery.tabGroupsOnlyHop}
          onClick={() => handleOnly(HOP.id)}
        >
          Only Hop Studios
        </button>
        <button
          type="button"
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted/60"
          style={{ borderColor: 'var(--line)' }}
          data-testid={testIds.maaterUiGallery.tabGroupsRestoreCollapsed}
          onClick={restoreHopCollapsed}
        >
          Restore Hop collapsed
        </button>
        <button
          type="button"
          className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted/60"
          style={{ borderColor: 'var(--line)' }}
          onClick={reset}
        >
          Reset
        </button>
      </div>

      <p className="text-xs" style={{ color: 'var(--ink-soft)' }}>
        Active scope:{' '}
        {state.activeIds.map((id) => (id === HOP.id ? 'Hop' : 'Personal')).join(' + ') ||
          'none'}
        . Collapsed:{' '}
        {[...state.collapsed].map((id) => (id === HOP.id ? 'HS' : 'P')).join(', ') || 'none'}
        .
      </p>
    </div>
  )
}
